import {Injectable} from '@angular/core';
import {ChildCaseModel} from '../model/child-case.model';
import {PictureService} from './picture.service';
import {AngularFirestore} from '@angular/fire/firestore';
import {ParseObjectUtil} from '../util/parse-object.util';
import {LoginService} from './login.service';
import {User} from '../model/user.model';
import {Observable} from 'rxjs/Observable';
import {DateUtil} from '../util/date.util';
import {SortUtil} from '../util/sort.util';
import {FilterUtil} from '../util/filter.util';
import {ChildModel} from '../model/child.model';
import {AlgoliaService} from './algolia.service';
import algoliasearch, {SearchIndex} from 'algoliasearch';
import {AlgoliaUtil} from '../util/algolia.util';
import {VisitDraftUtil} from '../util/visit-draft.util';
import {VaccinationDraftUtil} from '../util/vaccination-draft.util';
import {SymptomsDraftUtil} from '../util/symptoms-draft.util';
import {StepDraftUtil} from '../util/step-draft.util';

@Injectable({
    providedIn: 'root'
})
export class ChildCaseService {

    constructor(private pictureService: PictureService,
                private loginService: LoginService,
                private algoliaService: AlgoliaService,
                private db: AngularFirestore) {
        this.user = loginService.currentUser;
        const client = algoliasearch(AlgoliaUtil.APP_ID, AlgoliaUtil.getSearchApiKey());
        this.index = client.initIndex(AlgoliaUtil.getChildrenIndex());
    }

    static STEP_PROGRESS: number = 16.66;
    static FIRST_STEP: number = 16.66;

    public childCase: ChildCaseModel;
    currentFlowId: string | null = null;
    private registrationChild: ChildCaseModel;
    private authService = this.loginService;
    public childCaseEdit: ChildCaseModel;
    public childCaseDelegate: ChildCaseModel;
    public registryProgress: number = 16.66;
    public currentStep: number = 1;
    public user: User;
    public availableWizard: any[];
    private index: SearchIndex;

    public isNewVisit = false;

    get wizard() {
        if (!this.isNewVisit) {
            return this.newCaseWizard;
        } else {
            return this.newVisitWizard;
        }
    }

    public newCaseWizard = [
        {
            step: 1,
            active: this.registryProgress <= ChildCaseService.FIRST_STEP,
            progress: ChildCaseService.FIRST_STEP,
            title: 'Datos de registro'
        },
        {
            step: 2,
            active: this.registryProgress <= 33.32 && this.registryProgress > ChildCaseService.FIRST_STEP,
            progress: 33.32,
            title: 'Datos de la visita'
        },
        {
            step: 3,
            active: this.registryProgress <= 49.98 && this.registryProgress > 33.32,
            progress: 49.98,
            title: 'Sintomas'
        },
        {
            step: 4,
            active: this.registryProgress <= 66.64 && this.registryProgress > 49.98,
            progress: 66.64,
            title: 'Vacunación'
        },
        {
            step: 5,
            active: this.registryProgress <= 83.3 && this.registryProgress > 66.64,
            progress: 83.3,
            title: 'Desparasitación'
        },
        {
            step: 6,
            active: this.registryProgress <= 100 && this.registryProgress > 83.3,
            progress: 99.96,
            title: 'Resultado'
        }

    ];

    public newVisitWizard = [
        {
            step: 1,
            active: this.registryProgress <= ChildCaseService.FIRST_STEP,
            progress: ChildCaseService.FIRST_STEP,
            title: 'Datos de la visita'
        },
        {
            step: 2,
            active: this.registryProgress <= 33.32 && this.registryProgress > ChildCaseService.FIRST_STEP,
            progress: 33.32,
            title: 'Sintomas'
        },
        {
            step: 3,
            active: this.registryProgress <= 49.98 && this.registryProgress > 33.32,
            progress: 49.98,
            title: 'Vacunación'
        },
        {
            step: 4,
            active: this.registryProgress <= 66.64 && this.registryProgress > 49.98,
            progress: 66.64,
            title: 'Desparasitación'
        },
        {
            step: 5,
            active: this.registryProgress <= 100 && this.registryProgress > 49.98,
            progress: 100,
            title: 'Resultados'
        }
    ];

    getAge(date: Date) {
        return DateUtil.yearsDiff(date, new Date());
    }

    getMonths(date: Date) {
        return DateUtil.monthsDiff(date, new Date());
    }

    newVisit(childCase: ChildCaseModel) {
        this.childCase = childCase;
        this.childCase.transferredCase = false;
        // toda visita feita por este portal é protocolo novo — marca a raiz do
        // caso (casos antigos revisitados aqui passam a contar como protocolo novo)
        this.childCase.newProtocol = true;
        // medições da visita anterior não podem aparecer pré-preenchidas numa nova visita
        this.childCase.muac = null;
        this.childCase.weight = null;
        this.childCase.height = null;
        this.childCase.imc = null;
        this.childCase.date = null;
        this.childCase.comments = null;
        this.childCase.symptoms = [];
        this.childCase.anotherDisease = null;
        this.childCase.dewormingPeriod = '';
        // indicadores/resultados calculados na visita anterior
        this.childCase.level = '';
        this.childCase.rangeIndicator = '';
        this.childCase.weightLevel = '';
        this.childCase.weightRangeIndicator = '';
        this.childCase.heightLevel = '';
        this.childCase.heightRangeIndicator = '';
        this.childCase.imcLevel = '';
        this.childCase.imcRangeIndicator = '';
        this.childCase.heightToWeightLevel = '';
        this.childCase.heightToWeightIndicator = '';
        // rascunhos locais (sessionStorage) são da visita anterior — limpar todos.
        // As vacinas voltam a ser carregadas de child.vaccines (histórico acumulado),
        // por isso o rascunho de vacinação também é limpo aqui.
        VisitDraftUtil.clear(childCase?.token);
        VaccinationDraftUtil.clear(childCase?.token);
        SymptomsDraftUtil.clear(childCase?.token);
        StepDraftUtil.clear('deworming-draft', childCase?.token);
        StepDraftUtil.clear('results-draft', childCase?.token);
        this.isNewVisit = true;
        this.registryProgress = ChildCaseService.FIRST_STEP;
        this.availableWizard = this.wizard;
        this.currentStep = 1;
        console.log('Novo visita');
    }

    initCase() {
        if (!this.childCase) {
            console.log('Iniciando um novo caso');
            this.childCase = new ChildCaseModel(this.loginService.currentUser);
            this.childCase.currentHealthAssistant = this.authService.getCurrentUser();
            //Verify duplication
            this.childCase.assistantUid = this.authService.getCurrentUser()?.uid || 'unknown-uid';
            this.childCase.currentAssistantUid = this.authService.getCurrentUser()?.uid || 'unknown-uid';
            this.childCase.organizationName = this.authService.getCurrentUser()?.organizationName;
            // Set today as the visit date but it can be changed in future
            this.childCase.date = new Date();
            this.childCase.dateTimestamp = new Date().getTime();
            this.childCase.yearOfVisit = new Date().getFullYear();
            this.availableWizard = this.wizard;
            this.isNewVisit = false;
        }
    }

    findMyCases() {
        return this.db.collection<ChildCaseModel>('cases',
            ref => ref.where('healthAssistantsUids', 'array-contains', this.loginService.authUser?.uid)
                .orderBy('nextVisitDate', 'asc')).valueChanges();
    }

    find(sortBy?: SortUtil, filterBy?: FilterUtil) {
        if (!sortBy) {
            sortBy = new SortUtil('nextVisitDate', 'asc');
        }

        if (this.loginService.authUser.srs) {
            return this.findForSRS(sortBy, filterBy);
        } else if (this.loginService.authUser.organizationType?.includes('UNICEF')) {
            return this.findAll(sortBy, filterBy);
        } else if (this.loginService.authUser.dps) {
            return this.findForDPS(sortBy, filterBy);
        } else {
            return this.findOrganizationCases(sortBy, filterBy);
        }
    }

    // List by organization
    findOrganizationCases(sortBy: SortUtil, filterBy?: FilterUtil): Observable<ChildCaseModel[]> {
        if (filterBy) {
            console.log(filterBy);
            return this.db.collection<ChildCaseModel>('cases',
                ref => ref.where('organizationName', '==', this.loginService.authUser?.organizationName)
                    .where(filterBy.field, filterBy.option, filterBy.value)
                    .orderBy(sortBy.field, sortBy.direction)).valueChanges();
        }

        return this.db.collection<ChildCaseModel>('cases',
            ref => ref.where('organizationName', '==', this.loginService.authUser?.organizationName)
                .orderBy(sortBy.field, sortBy.direction)).valueChanges();
    }

    // List for national
    findAll(sortBy: SortUtil, filterBy?: FilterUtil) {
        if (filterBy) {
            return this.db.collection<ChildCaseModel>('cases',
                ref => ref.where(filterBy.field, filterBy.option, filterBy.value)
                    .orderBy(sortBy.field, sortBy.direction)).valueChanges();
        }

        return this.db.collection<ChildCaseModel>('cases',
            ref => ref.orderBy(sortBy.field, sortBy.direction)).valueChanges();
    }

    // List for SRS
    findForSRS(sortBy: SortUtil, filterBy?: FilterUtil) {
        const provinces = this.loginService.provinces.map(province => province.name);
        if (provinces.length === 0) {
            provinces.push('EMPTY');
        }
        return this.db.collection<ChildCaseModel>('cases',
            ref => ref.where('province', 'in', provinces).orderBy(sortBy.field, sortBy.direction)).valueChanges();
    }

    // List for SRS
    findForDPS(sortBy: SortUtil, filterBy?: FilterUtil) {
        const province = this.loginService.authUser.dps.provinceReference;
        if (filterBy) {
            return this.db.collection<ChildCaseModel>('cases',
                ref => ref.where('province', '==', province)
                    .where(filterBy.field, filterBy.option, filterBy.value)
                    .orderBy(sortBy.field, sortBy.direction)).valueChanges();
        }

        return this.db.collection<ChildCaseModel>('cases',
            ref => ref.where('province', '==', province).orderBy(sortBy.field, sortBy.direction)).valueChanges();
    }

    cleanCaseSpaces() {
        const propsCaseToClean = [
            'birthCertificateNumber',
            'caregiverName',
            'childAddress',
            'chronicDisease',
            'firstName',
            'lastName',
            'mothersName',
        ];

        propsCaseToClean.forEach(props => {
            if (this.childCase[props]) {
                this.childCase[props] = this.childCase[props].trim();
                this.childCase[props] = this.childCase[props].replace(/\s\s+/g, ' ');
            }
        });
    }

    /**
     * @returns true se o índice de busca (Algolia) já refletia esta gravação
     * quando a função retornou; false se desistiu depois de `waitForIndexSync`
     * (o Firestore FOI salvo normalmente — só a listagem que pode demorar mais
     * alguns segundos para atualizar). O caller usa isso só para decidir a
     * mensagem — nunca para saber se o caso foi salvo.
     */
    async save(): Promise<boolean> {
        const updateDate = new Date();
        this.childCase.updateDate = updateDate;
        this.childCase.appVersion = 'WEB';
        for (let i = 0; i < this.childCase.visits.length; i++) {
            if (this.childCase.visits[i].marker != 'no-measured' && !this.childCase.visits[i].date) {
                this.childCase.visits[i].date = this.childCase.date;
            }
        }
        this.cleanCaseSpaces();

        if (this.childCase.visits.filter(visit => visit.date).length > 1) {
            await this.update();
        } else {
            if (this.childCase.childSelfie && this.childCase.childSelfie.includes('data')) {
                await fetch(this.childCase.childSelfie).then(async response => {
                    this.childCase.childSelfieRef = await this.pictureService.upload(await response.blob());
                    this.childCase.childSelfie = await this.pictureService.getPictureURL(this.childCase.childSelfieRef) as string;
                    return this.caseRegistry();
                });
            } else {
                this.childCase.childSelfie = await this.pictureService.getPictureURL(PictureService.NO_PROFILE_PICTURE) as string;
                await this.caseRegistry();
            }
        }
        // Neste ponto o Firestore JÁ FOI GRAVADO (o `await` acima só resolve depois
        // do ack do servidor) — a visita está salva independente do que acontece
        // daqui pra baixo.

        // O índice de busca (Algolia) que alimenta "Mis casos"/"Niños" é
        // sincronizado pelo backend com alguns segundos de atraso — sem isso o
        // usuário volta pra lista e o caso aparece com o estado ANTERIOR até a
        // próxima interação (trocar filtro, etc). Espera o índice refletir esta
        // gravação (checando updateDate) antes de liberar a navegação; se a
        // conexão já caiu não adianta tentar (offline == sem Algolia também).
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return false;
        }
        return this.waitForIndexSync(this.childCase.token, updateDate.toISOString());
    }

    /**
     * Poll curto no índice do Algolia até `updateDate` bater com o que acabou de
     * ser salvo, ou até `maxWaitMs` esgotar. Nunca lança erro e nunca trava além
     * do prazo — na pior hipótese a navegação segue sem esperar (o índice
     * sincroniza sozinho, como sempre; só devolve false para o caller avisar
     * o usuário que a lista pode demorar mais alguns segundos).
     */
    private async waitForIndexSync(token: string, expectedUpdateDate: string, maxWaitMs: number = 8000): Promise<boolean> {
        if (!token) {
            return true;
        }
        const stepMs = 700;
        const deadline = Date.now() + maxWaitMs;
        while (Date.now() < deadline) {
            try {
                const obj: any = await this.index.getObject(token);
                if (obj && obj.updateDate === expectedUpdateDate) {
                    return true;
                }
            } catch (e) {
                // objeto pode ainda não existir no índice (caso novo, 1ª gravação) — tenta de novo
            }
            await new Promise(resolve => setTimeout(resolve, stepMs));
        }
        return false;
    }

    caseRegistry() {
        if (!this.childCase.childName) {
            this.childCase.childName = this.childCase.firstName + ' ' + this.childCase.lastName;
            this.childCase.childToken = this.childCase.token;
        }
        const child = new ChildModel();
        child.updateWithCase(this.childCase);
        this.db.collection('children').doc(this.childCase.token).set(ParseObjectUtil.parse(child));

        return this.db.collection('cases').doc(this.childCase.token).set(ParseObjectUtil.parse(this.childCase));
    }

    update(childCase?: ChildCaseModel) {
        if (childCase) {
            return this.db.collection<ChildCaseModel>('cases').doc(childCase.token)
                .set(ParseObjectUtil.parse(childCase), {merge: true});
        }
        return this.db.collection<ChildCaseModel>('cases').doc(this.childCase.token)
            .set(ParseObjectUtil.parse(this.childCase), {merge: true});
    }

    async edit() {
        if (this.childCaseEdit.childSelfie && this.childCaseEdit.childSelfie.includes('data')) {
            await fetch(this.childCaseEdit.childSelfie).then(async response => {
                this.childCaseEdit.childSelfieRef = await this.pictureService.upload(await response.blob());
                this.childCaseEdit.childSelfie = await this.pictureService.getPictureURL(this.childCaseEdit.childSelfieRef) as string;

                return this.db.collection<ChildCaseModel>('cases').doc(this.childCaseEdit.token)
                    .set(ParseObjectUtil.parse(this.childCaseEdit), {merge: true});
            });
        }
        return this.db.collection<ChildCaseModel>('cases').doc(this.childCaseEdit.token)
            .set(ParseObjectUtil.parse(this.childCaseEdit), {merge: true});
    }

    cancelCaseRegistry() {
        // limpa rascunhos locais das etapas (concluído ou cancelado)
        VisitDraftUtil.clear(this.childCase?.token);
        VaccinationDraftUtil.clear(this.childCase?.token);
        SymptomsDraftUtil.clear(this.childCase?.token);
        StepDraftUtil.clear('deworming-draft', this.childCase?.token);
        StepDraftUtil.clear('results-draft', this.childCase?.token);
        this.childCase = null;
        this.isNewVisit = false;
        this.currentStep = 1;
        this.registryProgress = ChildCaseService.FIRST_STEP;
        this.availableWizard = this.wizard;
        this.availableWizard.forEach(step => step.active = step.step === this.currentStep);
    }

    public nextStep() {
        if (this.registryProgress < 100 && !this.availableWizard[this.currentStep].active) {
            this.registryProgress += (this.availableWizard.length === this.wizard.length) ?
                ChildCaseService.STEP_PROGRESS : ChildCaseService.FIRST_STEP;

            this.registryProgress = Number(this.registryProgress.toFixed(2));
        }

        if (this.currentStep < this.availableWizard.length) {
            this.currentStep++;
        }

        if (this.registryProgress === 99) {
            this.registryProgress = 100;
        }

        if (this.currentStep == 5 && this.isNewVisit) {
            this.registryProgress = 100;
        }

        this.availableWizard.forEach(step => {
            step.active = step.progress === this.registryProgress;
        });

        window.scrollTo({top: 0});
    }

    public previousStep() {
        if (this.registryProgress > ChildCaseService.FIRST_STEP && !this.availableWizard[this.currentStep]?.active) {
            this.registryProgress -= (this.availableWizard.length === this.wizard.length) ?
                ChildCaseService.STEP_PROGRESS : ChildCaseService.FIRST_STEP;
            this.registryProgress = Number(this.registryProgress.toFixed(2));
            this.currentStep--;
        }

        if (this.currentStep > ChildCaseService.FIRST_STEP) {
            this.currentStep--;
        }

        this.availableWizard.forEach(step => {
            step.active = step.progress === this.registryProgress;
        });

        window.scrollTo({top: 0});
    }

    findForUnapSupervisor() {
        const organization = this.user.organizationName || 'invalid';
        return this.db.collection<ChildCaseModel>('/cases/', ref =>
            ref.where('organizationName', '==', organization)
                .orderBy('nextVisitDate', 'asc')).valueChanges();
    }


    public async indexAllCases() {
        this.db.firestore.collection('cases')
            .where('marker', '==', 'outsite-risk-zone')
            // .where('updated', '!=', true)
            .limit(1000) // retorna no máximo 10 documentos
            .get()
            .then(values => {
                console.log('Resultado:', values.size);
                for (let i = 0; i < values.docs.length; i++) {
                    const value = values.docs[i].data();
                    if (!value.updated) {
                        value.updated = true;
                        value.currentAssistantUid = value.assistantUid;
                        this.db.collection('cases').doc(value.token).set(ParseObjectUtil.parse(value)).then(() => {
                            console.log('Registro ' + i + ' atualizado');
                        });
                    }
                }
            })
            .catch(error => {
                console.error('Erro na query:', error);
            });

    }

    // Filter by next visit

    public getNextSevenDaysCases() {
        const inSevenDays = DateUtil.addDays(new Date(), 7);
        return this.index.search('', {
            filters: `nextVisitTimestamp >= ${DateUtil.nowTimestamp()} AND nextVisitTimestamp <= ${inSevenDays.getTime()}`
        });
    }

}
